import { useMemo } from 'react'

import { MainContent, ModalContext, TopBar } from '@domain-group/suit-theme'

import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import useMainNavigation from '@hooks/useMainNavigation'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrEditTaskListModal from '@modals/CreateOrEditTaskListModal'
import CreateTemplateGroupModal from '@modals/CreateTemplateGroupModal'

import DashboardSearch from '@components/DashboardSearch'
import LoggedInUser from '@components/LoggedInUser'
import MainNavigation from '@sections/Admin/components/MainNavigation'

import defaultRequestOptions from '../../defaultRequestOptions'

import PageContent from './components/PageContent'

export type PageContextPayload = {
  callbacks: {
    resetFilters: () => void,
    showCreateOrEditTaskListModal: (payload: object) => void,
    showCreateTemplateGroupModal: () => void,
    updateFilters: (filters: object) => void,
  },
  defaultFilters: object,
}

const callbacks = (component: string, setState) => {
  const componentCallbacks = {
    CreateOrEditTaskListModal: {
      closeModal: () => setState({ showCreateOrEditTaskListModal: false }),
      createTaskList: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      createUsingUsageReport: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      toggleTaskListCompletion: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateTemplateGroupModal: {
      closeModal: () => setState({ showCreateTemplateGroupModal: false }),
      createTemplateGroup: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const defaultState = {
  showCreateOrEditTaskListModal: false,
  showCreateTemplateGroupModal: false,
}

const Overview = () => {
  const [state, setState] = useSetState(defaultState)
  const { showCreateOrEditTaskListModal, showCreateTemplateGroupModal } = state

  const { callbacks: { toggleNavigation } } = useMainNavigation()

  const {
    callbacks: filterCallbacks,
    defaultFilters,
    pageFilters,
  } = useFilters()

  const modalContext = useModals()
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showCreateOrEditTaskListModal: (payload: object) => {
        setModalData('CreateOrEditTaskListModal', payload)
        setState({ showCreateOrEditTaskListModal: true })
      },
      showCreateTemplateGroupModal: () => setState({ showCreateTemplateGroupModal: true }),
    },
    defaultFilters,
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <TopBar
          callbacks={{ toggleNavigation }}
          loggedInUserComponent={<LoggedInUser />}
          searchComponent={<DashboardSearch searchEntities={['LayoutTemplate', 'TaskList', 'TemplatePage', 'TemplatePagePartial']} />}
        />

        <MainNavigation />

        <MainContent offset={{ top: TopBar.topBarHeight }}>
          <PageContent pageFilters={pageFilters} />
        </MainContent>

        <CreateOrEditTaskListModal
          callbacks={callbacks('CreateOrEditTaskListModal', setState)}
          requestOptions={{
            ...defaultRequestOptions.taskList,
            include_task_list_task_counts: true,
          }}
          showModal={showCreateOrEditTaskListModal}
        />

        <CreateTemplateGroupModal
          callbacks={callbacks('CreateTemplateGroupModal', setState)}
          requestOptions={{
            ...defaultRequestOptions.taskList,
            ...defaultRequestOptions.templateGroup,
            include_task_list_task_counts: true,
          }}
          showModal={showCreateTemplateGroupModal}
          titleSecondLine="Project"
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Overview
